import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  UiBox,
  UiDivider,
  UiIconButton,
  UiIconChatGpt,
  UiIconPolotno,
  UiLink,
  UiList,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import {
  IconBrandGoogleAnalytics,
  IconBusinessplan,
  IconCalendarTime,
  // IconCast,
  IconCreditCard,
  IconFolder,
  IconHelp,
  IconLayoutCollage,
  IconMenu2,
  IconRss,
  IconSchool,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import clsx from "clsx";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
// import { useContextLoader } from "@/context/loader";
import { useContextApp } from "@/screens/app/context/app";
import { yaReachGoal } from "@/utils/ya_reach_goal";

import { useStyles } from "./drawer.css";
import { DrawerItem } from "./drawer_item";
import { Time } from "./time";

export const Drawer = () => {
  const location = useLocation();

  const {
    actions: { drawerExpandToggle },
    state: { isExpanded, isPersonalOrOwnOrganizationContext },
  } = useContextApp();

  // const {
  //   state: { config },
  // } = useContextLoader();

  const classes = useStyles();
  const {
    i18n: { locale },
  } = useLingui();

  return useMemo(
    () => (
      <div className={classes["drawer-wrapper"]}>
        <div
          className={clsx(classes.drawer, {
            [classes["drawer--expanded"]]: isExpanded,
          })}
        >
          <div className={classes.drawer__header}>
            <UiIconButton onClick={() => drawerExpandToggle()}>
              <IconMenu2 className={classes.drawer__header_icon} />
            </UiIconButton>
            {isExpanded && (
              <UiBox
                alignItems="center"
                display="flex"
                justifyContent="flex-start"
                flexGrow={1}
                paddingLeft={1}
              >
                <UiLink component={HelperAdapterLink} to="/">
                  <UiTypography
                    className={classes.drawer__header_logo}
                    variant="h4"
                  >
                    SMMplanner
                  </UiTypography>
                </UiLink>
              </UiBox>
            )}
          </div>

          <UiBox className={classes.drawer__list} isCustomScrollbar>
            <UiList>
              <DrawerItem
                Icon={IconCalendarTime}
                isExpanded={isExpanded}
                text={
                  <>
                    <Trans>Posts</Trans>
                    {" New"}
                  </>
                }
                to="/new_posts"
                toAlt="/new_posts"
                hasBadge
                {...testAppendAttr("id", "new_posts")}
              />
              <DrawerItem
                Icon={IconCalendarTime}
                isExpanded={isExpanded}
                text={<Trans>Posts</Trans>}
                to="/posts"
                toAlt="/sc"
                {...testAppendAttr("id", "posts")}
              />
              <DrawerItem
                Icon={IconBrandGoogleAnalytics}
                isExpanded={isExpanded}
                text={<Trans>Analytics</Trans>}
                to="/reports"
                onClick={() => yaReachGoal("navigation_reports")}
                {...testAppendAttr("id", "reports")}
              />
              <DrawerItem
                Icon={UiIconChatGpt}
                isExpanded={isExpanded}
                text={<Trans>AI assistant</Trans>}
                to="/assistant"
                {...testAppendAttr("id", "assistant")}
              />
              <DrawerItem
                Icon={UiIconPolotno}
                isExpanded={isExpanded}
                text={<Trans>Canvas</Trans>}
                to="/polotno"
                state={{
                  prevPath: location.pathname,
                }}
                hasBadge
                {...testAppendAttr("id", "polotno")}
              />
              <DrawerItem
                Icon={IconLayoutCollage}
                isExpanded={isExpanded}
                text={<Trans>Templates</Trans>}
                to="/templates"
                hasBadge
                {...testAppendAttr("id", "templates")}
              />
              <DrawerItem
                Icon={IconFolder}
                isExpanded={isExpanded}
                text={<Trans>Social Sets</Trans>}
                to={"/projects"}
                {...testAppendAttr("id", "projects")}
              />
              <DrawerItem
                Icon={IconRss}
                isExpanded={isExpanded}
                text={<Trans>Reposter</Trans>}
                to="/repost"
                {...testAppendAttr("id", "repost")}
              />
              {/*{config.billing.is_stream_visible && (*/}
              {/*  <DrawerItem*/}
              {/*    Icon={IconCast}*/}
              {/*    isExpanded={isExpanded}*/}
              {/*    text={<Trans>Re-stream</Trans>}*/}
              {/*    to="/streams"*/}
              {/*    {...testAppendAttr("id", "streams")}*/}
              {/*  />*/}
              {/*)}*/}
              <DrawerItem
                Icon={IconUserPlus}
                isExpanded={isExpanded}
                text={<Trans>Accounts</Trans>}
                to="/accounts"
                {...testAppendAttr("id", "accounts")}
              />
              <DrawerItem
                Icon={IconUsers}
                isExpanded={isExpanded}
                text={<Trans>Teams</Trans>}
                to="/team"
                {...testAppendAttr("id", "team")}
              />
              {isPersonalOrOwnOrganizationContext && (
                <>
                  <DrawerItem
                    Icon={IconCreditCard}
                    isExpanded={isExpanded}
                    text={<Trans>Billing</Trans>}
                    to="/billing"
                    {...testAppendAttr("id", "billing")}
                  />
                </>
              )}
              <DrawerItem
                Icon={IconBusinessplan}
                isExpanded={isExpanded}
                text={<Trans>__drawer__Affiliate_program</Trans>}
                to="/affiliate_program"
                {...testAppendAttr("id", "affiliate_program")}
              />
            </UiList>
          </UiBox>
          <div className={classes.drawer__time}>
            {locale === "ru" && (
              <DrawerItem
                Icon={IconSchool}
                isExpanded={isExpanded}
                text={<Trans>Academy</Trans>}
                target="_blank"
                to="/academy"
                href="/academy"
                component="a"
                {...testAppendAttr("id", "academy")}
              />
            )}
            <DrawerItem
              Icon={IconHelp}
              isExpanded={isExpanded}
              text={<Trans>Need help?</Trans>}
              to="/help"
              {...testAppendAttr("id", "help")}
            />
            <UiDivider />
            <Time />
          </div>
        </div>

        <div
          className={classes.overlay}
          onClick={() => drawerExpandToggle(false)}
        />
      </div>
    ),
    [
      classes,
      drawerExpandToggle,
      isExpanded,
      isPersonalOrOwnOrganizationContext,
      locale,
      location.pathname,
    ],
  );
};
